import React from 'react';
import { Redirect, useParams } from 'react-router-dom';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import FullScreenLoader from '../../Loader/FullScreenLoader';
import { ROUTES } from '../../../routing';

/**
 * Guards the assessment-test admin routes. When the current company does not
 * have the `ASSESSMENT_TEST` setting enabled, these pages should not be
 * accessible, so we redirect to the company's admin home (Progress) instead of
 * rendering empty pages.
 *
 * Uses the same data source as `CompanySideNavigation` (`useGetCompany`) so the
 * guard stays consistent with which nav items are shown.
 */
export default function AssessmentTestGuard({
  children,
}: {
  children: React.ReactNode;
}) {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useGetCompany(parseInt(id));

  // Wait until we know whether the feature is enabled before deciding. While
  // loading (or on error) let the wrapped page handle its own loading/error
  // states rather than redirecting prematurely.
  if (isLoading || error || !data) {
    if (isLoading) {
      return <FullScreenLoader />;
    }
    return <>{children}</>;
  }

  if (!data.settings?.ASSESSMENT_TEST) {
    return <Redirect to={ROUTES.ADMIN.PROGRESS.getPath({ companyId: id })} />;
  }

  return <>{children}</>;
}
